import { TL } from '@/components/design-system'
import { InformationEntreprise } from '@/components/presentation-links/InformationEntreprise'
import { useDownloadCalendrierLien } from '@/components/presentation-links/use-download-calendrier-lien'
import { isBefore } from 'date-fns'
import { format } from 'date-fns-tz'
import { enUS, fr } from 'date-fns/locale'
import type { HeadFC, PageProps } from 'gatsby'
import { graphql, HeadProps } from 'gatsby'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import * as React from 'react'
import Layout from '../../components/Layout'
import SEO from '../../components/SEO'

const PresentationLinksWebinarDDDLegacy: React.FC<PageProps> = (props: PageProps) => {
  const { t, i18n } = useTranslation('presentationLinks', { keyPrefix: 'podcastTechLeadCorner' })
  const locale = i18n.resolvedLanguage === 'fr' ? fr : enUS
  const timeZone = 'Europe/Paris'
  const onDownloadCalendrierLien = useDownloadCalendrierLien(t('metadata.titre'), t('metadata.description'), new Date(t('prenez-rdv.date.valeur')!), {
    heures: 1,
    minutes: 0
  })

  // @ts-ignore
  const images = props.data.images.edges.map(({ node }) => node.resize)
  const calendar = images.find(i => i.originalName === 'calendar_clock.png')

  const nEstPasPasse = isBefore(new Date(), new Date(t('prenez-rdv.date.valeur')!))
  const afficherPlaceholder = t('liens.presentation.lien') === ''

  return (
      <Layout>
        <article className="flex flex-col gap-5 mt-48 mx-auto max-w-7xl max-xl:mx-20 max-sm:mx-7">
          <div className="grid grid-cols-3 max-md:grid-cols-2">
            <div className="flex items-center col-span-2 max-xl:col-span-3 max-md:flex-col max-md:gap-10">
              <div className="pr-5">
                <TL.Title size="h1">{ t('titre') }</TL.Title>
                <p
                    className="italic text-lg"
                    dangerouslySetInnerHTML={ {
                      __html: t('sponsorise')
                          .replace('[LIEN_1]', '<a class="hover:text-digitalblue" href="https://www.packmind.com//" target="_blank">Packmind (ex Promyze)</a>')
                    } }>
                </p>
              </div>
            </div>
          </div>

          <section className="flex flex-col gap-2 text-lg mt-14">
            <p>{ t('presentation.texte-1') }</p>
            <p>{ t('presentation.texte-2') }</p>
          </section>

          { nEstPasPasse ? (
              <section className="my-10">
                <TL.Title className="text-center" size="h2">{ t('prenez-rdv.titre') }</TL.Title>

                <div className="grid grid-cols-2 gap-10 mt-8 grid-flow-dense max-lg:grid-cols-1">
                  { calendar &&
                      <div className="flex justify-center items-center">
                        <img
                            className="cursor-pointer"
                            src={ calendar.src }
                            alt="calendar logo"
                            width={ 150 }
                            height={ 150 }
                            onClick={ onDownloadCalendrierLien }/>
                      </div>
                  }

                  <div className="flex flex-col justify-center gap-2">
                    <p dangerouslySetInnerHTML={ {
                      __html: t('prenez-rdv.texte')
                          .replace('[DATE]', format(
                              new Date(t('prenez-rdv.date.valeur')!),
                              t('prenez-rdv.date.format'),
                              {
                                locale,
                                timeZone
                              }
                          ))
                    } }/>
                    <a className="hover:text-digitalblue" href={ t('prenez-rdv.lien.url') }
                       target="_blank">{ t('prenez-rdv.lien.texte') }</a>
                  </div>
                </div>
              </section>
          ) : (
              <section className="my-10">
                <TL.Title className="text-center" size="h2">{ t('liens.titre') }</TL.Title>

                { afficherPlaceholder && <div className="mt-8"><p>{ t('liens.placeholder') }</p></div> }

                { !afficherPlaceholder &&
                    <div className="mt-8 mx-auto max-w-2xl">
                      <iframe name="Ausha Podcast Player"
                              loading="lazy"
                              id="ausha-lY7T"
                              className="w-full h-[220px] max-[555px]:h-[501px]"
                              src="https://player.ausha.co/?podcastId=7zq71SEQ6dY9&playlist=false&color=%23A6ACFB&v=3&playerId=ausha-lY7T"></iframe>
                      <script src="https://player.ausha.co/ausha-player.js"></script>
                    </div>
                }
              </section>
          ) }

          <InformationEntreprise/>
        </article>
      </Layout>
  )
}

export default PresentationLinksWebinarDDDLegacy

export const Head: HeadFC<HeadProps> = (props) => {
  // @ts-ignore
  const common = props.data.locales.edges.find(e => e.node.ns === 'common')
  // @ts-ignore
  const page = props.data.locales.edges.find(e => e.node.ns === 'presentationLinks')

  const metadataCommon = JSON.parse(common.node.data).metadata
  const metadata = JSON.parse(page.node.data).webinarDDDLegacy.metadata

  // @ts-ignore
  const locale = props.pageContext.language

  return <SEO
      metadata={ { ...metadata, ...metadataCommon } }
      path={ props.location.pathname }
      locale={ locale }/>
}

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "presentationLinks"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    images: allImageSharp {
      edges {
        node {
          resize {
            originalName
            src
          }
        }
      }
    }
  }
`
